import { supabase } from '@/integrations/supabase/client';

interface ErrorContext {
  component?: string;
  action?: string;
  url?: string;
  [key: string]: unknown;
}

class ErrorTracker {
  private queue: Array<{ level: string; message: string; context: ErrorContext; stack?: string }> = [];
  private flushing = false;

  init() {
    window.addEventListener('error', (event) => {
      this.capture('error', event.message || 'Unhandled error', {
        url: event.filename,
        line: event.lineno,
        col: event.colno,
      }, event.error?.stack);
    });

    window.addEventListener('unhandledrejection', (event) => {
      const message = event.reason?.message || String(event.reason) || 'Unhandled promise rejection';
      this.capture('error', message, {
        action: 'unhandled_rejection',
      }, event.reason?.stack);
    });
  }

  capture(level: 'error' | 'warn' | 'info', message: string, context: ErrorContext = {}, stack?: string) {
    // Skip noise
    if (message.includes('ResizeObserver') || message.includes('Loading chunk')) return;

    const enrichedContext = {
      ...context,
      url: context.url || window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    this.queue.push({ level, message: message.slice(0, 500), context: enrichedContext, stack: stack?.slice(0, 2000) });

    // Debounce flush
    if (!this.flushing) {
      this.flushing = true;
      setTimeout(() => this.flush(), 2000);
    }
  }

  private async flush() {
    const batch = this.queue.splice(0, 10);
    this.flushing = false;

    if (!batch.length) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const rows = batch.map((e) => ({
        level: e.level,
        message: e.message,
        context: e.context as unknown as import('@/integrations/supabase/types').Json,
        stack_trace: e.stack || '',
        source: 'frontend',
        user_id: user?.id || null,
      }));

      await supabase.from('error_log').insert(rows);
    } catch {
      // Don't recurse on error tracking errors
      console.warn('[ErrorTracker] Failed to flush errors');
    }
  }

  captureException(error: Error, context: ErrorContext = {}) {
    this.capture('error', error.message, context, error.stack);
  }
}

export const errorTracker = new ErrorTracker();
